import type {
  InternalEmail,
  InternalEmailDocument,
  InternalEmailDocuments,
  InternalEmailScorecards,
} from '@/api/internalEmails';

/**
 * What the sender picked in the attach-files popup.
 *
 * Existing candidate documents are held as *references* — an id and a name, no
 * file contents — and are only read server-side when the email goes out. Only
 * `uploads` carries real bytes, because those files came from this device.
 */
/** A file already attached to the email being edited, named so it can be shown. */
export interface KeptAttachment {
  blobId: number;
  filename: string;
  contentType?: string;
  byteSize?: number;
  url?: string;
}

export interface AttachmentSelection {
  atsAttachmentIds: number[];
  offerDocumentBlobIds: number[];
  includeResume: boolean;
  includeScorecards: boolean;
  uploads: File[];
  /**
   * Files already on the email being edited. They are not picker rows — the picker
   * only offers the candidate's documents — so they are held apart and sent back by
   * blob id, which is the only way an edit keeps them: the save rebuilds the email
   * from the composition it is given.
   */
  kept: KeptAttachment[];
  /**
   * The library template whose files `kept` currently holds, when one was applied.
   * Sent with the composition: a template's attachments are not the candidate's, so
   * the API needs telling which template they came from before it will accept them.
   */
  appliedTemplateId?: number | string | null;
}

export const EMPTY_SELECTION: AttachmentSelection = {
  atsAttachmentIds: [],
  offerDocumentBlobIds: [],
  includeResume: false,
  includeScorecards: false,
  uploads: [],
  kept: [],
  appliedTemplateId: null,
};

export const RESUME_KEY = 'resume';
export const SCORECARDS_KEY = 'scorecards';

/**
 * A card below the composer. `key` identifies it for removal.
 *
 * Size and type come from whatever the source knows: the picker's documents carry
 * them on the payload, an upload carries them on the `File`. The scorecards export
 * has neither until it is generated at send time, so it renders as a card with a
 * name and nothing else — see `SelectedAttachmentChips`.
 */
export interface AttachmentChip {
  key: string;
  name: string;
  contentType?: string;
  byteSize?: number;
  /** Absent for anything there is no file to open yet. */
  url?: string;
  /**
   * An upload's own bytes. It has no `url` — nothing has been uploaded yet — so
   * this is the only handle a preview has on it.
   */
  file?: File;
  /**
   * The file is still being produced server-side, so there is nothing to size, open
   * or trust the type of yet. Only the scorecards export is ever in this state.
   */
  loading?: boolean;
}

const documentChip = (
  key: string,
  fallbackName: string,
  document?: InternalEmailDocument
): AttachmentChip => ({
  key,
  name: document?.filename ?? fallbackName,
  contentType: document?.content_type,
  byteSize: document?.byte_size,
  url: document?.url,
});

const SCORECARDS_LABEL = 'All previous scorecards';

/**
 * The scorecards export, which is the one row that may still be rendering. Once it
 * lands it is an ordinary file and the card shows its size and a link; until then it
 * is named after what the sender picked, with a loader where the file will be.
 */
const scorecardsChip = (
  scorecards?: InternalEmailScorecards | null
): AttachmentChip => {
  // The label the sender picked, not the filename recipients will see: the card must
  // not rename itself the moment the render lands.
  const chip = { key: SCORECARDS_KEY, name: SCORECARDS_LABEL };

  if (scorecards?.status === 'ready') {
    return {
      ...chip,
      contentType: scorecards.content_type,
      byteSize: scorecards.byte_size,
      url: scorecards.url,
    };
  }

  return scorecards?.status === 'generating'
    ? { ...chip, loading: true }
    : chip;
};

export const offerKey = (blobId: number) => `offer:${blobId}`;
export const attachmentKey = (id: number) => `attachment:${id}`;
const uploadKey = (index: number) => `upload:${index}`;
export const keptKey = (blobId: number) => `kept:${blobId}`;

export const OFFER_DOCUMENTS_GROUP = 'Offer documents';
export const CANDIDATE_DETAILS_GROUP = 'Candidate details';
export const OTHER_FILES_GROUP = 'Other files';

const RESUME_LABEL = "Candidate's resume";

export interface AttachOption {
  value: string;
  label: string;
  group: string;
}

/**
 * The candidate's documents as picker rows, grouped and in the order the
 * headings are drawn — the control neither sorts nor regroups, and reports a
 * committed selection back in this same order.
 *
 * Values are the chip keys, so one vocabulary identifies a document whether it
 * is being picked, listed or removed.
 */
export const toAttachOptions = (
  documents: InternalEmailDocuments | undefined
): AttachOption[] => {
  const options: AttachOption[] = [];
  // The blobs already rowed above the "Other files" group. The resume's own file
  // can also sit on the application as an "other" attachment — add-to-files
  // writes one — and the API listed it under both kinds, so the one file drew in
  // two sections. Its own place wins; the API dedupes too (core#5956), this holds
  // against one that has not.
  const rowed = new Set<number>();

  (documents?.offer_documents ?? []).forEach((document) => {
    if (document.blob_id === undefined) return;
    rowed.add(document.blob_id);
    options.push({
      value: offerKey(document.blob_id),
      label: document.filename ?? 'Offer document',
      group: OFFER_DOCUMENTS_GROUP,
    });
  });

  if (documents?.scorecards_available) {
    options.push({
      value: SCORECARDS_KEY,
      label: SCORECARDS_LABEL,
      group: CANDIDATE_DETAILS_GROUP,
    });
  }

  if (documents?.resume) {
    if (documents.resume.blob_id !== undefined)
      rowed.add(documents.resume.blob_id);
    options.push({
      value: RESUME_KEY,
      label: documents.resume.filename ?? RESUME_LABEL,
      group: CANDIDATE_DETAILS_GROUP,
    });
  }

  (documents?.other_files ?? []).forEach((document) => {
    if (document.ats_attachment_id === undefined) return;
    if (document.blob_id !== undefined && rowed.has(document.blob_id)) return;
    options.push({
      value: attachmentKey(document.ats_attachment_id),
      label: document.filename ?? 'Document',
      group: OTHER_FILES_GROUP,
    });
  });

  return options;
};

/** The picked documents as picker values. Uploads are not rows, so not here. */
export const toSelectedValues = (selection: AttachmentSelection): string[] => [
  ...selection.offerDocumentBlobIds.map(offerKey),
  ...(selection.includeScorecards ? [SCORECARDS_KEY] : []),
  ...(selection.includeResume ? [RESUME_KEY] : []),
  ...selection.atsAttachmentIds.map(attachmentKey),
];

/**
 * A committed picker selection back into the send's shape.
 *
 * The picker owns the four document fields and nothing else: uploads, the files
 * already on the email or brought by a template, and the template they came from
 * all pass through untouched. Rebuilding the selection from the four alone lost
 * `appliedTemplateId` while keeping the template's files — so a send that applied
 * a template and then picked a document went out naming files the API could not
 * place, and was refused with err_internal_email_attachment_not_found.
 */
export const fromSelectedValues = (
  values: string[],
  selection: AttachmentSelection
): AttachmentSelection => ({
  ...selection,
  offerDocumentBlobIds: values
    .filter((value) => value.startsWith('offer:'))
    .map((value) => Number(value.slice('offer:'.length))),
  atsAttachmentIds: values
    .filter((value) => value.startsWith('attachment:'))
    .map((value) => Number(value.slice('attachment:'.length))),
  includeResume: values.includes(RESUME_KEY),
  includeScorecards: values.includes(SCORECARDS_KEY),
});

/**
 * The chips shown for a selection, in a stable order so removing one does not
 * reshuffle the rest.
 */
export const toAttachmentChips = (
  selection: AttachmentSelection,
  documents: InternalEmailDocuments | undefined
): AttachmentChip[] => {
  const chips: AttachmentChip[] = [];
  const byBlobId = new Map<number, InternalEmailDocument>();
  const byAttachmentId = new Map<number, InternalEmailDocument>();

  (documents?.offer_documents ?? []).forEach((document) => {
    if (document.blob_id !== undefined)
      byBlobId.set(document.blob_id, document);
  });
  (documents?.other_files ?? []).forEach((document) => {
    if (document.ats_attachment_id !== undefined) {
      byAttachmentId.set(document.ats_attachment_id, document);
    }
  });

  selection.offerDocumentBlobIds.forEach((blobId) => {
    chips.push(
      documentChip(offerKey(blobId), 'Offer document', byBlobId.get(blobId))
    );
  });

  if (selection.includeResume) {
    chips.push(
      documentChip(RESUME_KEY, 'Resume', documents?.resume ?? undefined)
    );
  }

  if (selection.includeScorecards) {
    chips.push(scorecardsChip(documents?.scorecards));
  }

  selection.atsAttachmentIds.forEach((id) => {
    chips.push(
      documentChip(attachmentKey(id), 'Document', byAttachmentId.get(id))
    );
  });

  selection.kept.forEach((attachment) => {
    chips.push({
      key: keptKey(attachment.blobId),
      name: attachment.filename,
      contentType: attachment.contentType,
      byteSize: attachment.byteSize,
      url: attachment.url,
    });
  });

  // Not yet uploaded anywhere, so the only handle is the File itself.
  selection.uploads.forEach((file, index) => {
    chips.push({
      key: uploadKey(index),
      name: file.name,
      contentType: file.type,
      byteSize: file.size,
      file,
    });
  });

  return chips;
};

/**
 * A template's document kinds, resolved against what this candidate actually has.
 *
 * The template says "a send from me attaches the resume"; here that becomes the resume
 * row ticked, if there is one. Intersected with the offered documents on purpose: the
 * activity decides which kinds it offers at all, and a blob outside that set is refused
 * at send (`AttachmentNotPermitted`) — so ticking one would promise something the send
 * cannot keep.
 *
 * Replaces the picked documents the way a template replaces the subject and body.
 * Uploads and `kept` files are the sender's own and are left alone.
 */
export const applyTemplateDocumentKinds = (
  selection: AttachmentSelection,
  kinds: string[] | undefined,
  documents: InternalEmailDocuments | undefined
): AttachmentSelection => {
  if (!kinds?.length) return selection;

  const wants = new Set(kinds);

  return {
    ...selection,
    offerDocumentBlobIds: wants.has('offer_documents')
      ? (documents?.offer_documents ?? [])
          .map((document) => document.blob_id)
          .filter((blobId): blobId is number => blobId !== undefined)
      : [],
    atsAttachmentIds: wants.has('other_files')
      ? (documents?.other_files ?? [])
          .map((document) => document.ats_attachment_id)
          .filter((id): id is number => id !== undefined)
      : [],
    includeResume: wants.has('resume') && Boolean(documents?.resume),
    // Unlike the rest this one is not a document the candidate already has — it is
    // rendered on demand — so it is ticked whenever there is feedback to render.
    includeScorecards:
      wants.has('scorecards') &&
      documents?.scorecards?.status !== 'unavailable' &&
      (Boolean(documents?.scorecards) ||
        Boolean(documents?.scorecards_available)),
  };
};

/**
 * The files a picked template carries, folded into the selection.
 *
 * A template's attachments go out with every send of it, so applying one brings
 * them along the way it brings the subject and body — and replaces the files the
 * previous template (or, on an edit, the email itself) had put there, for the same
 * reason. Uploads are the sender's own and are left alone.
 *
 * The template id rides along because the API cannot otherwise tell these blobs
 * from any other file the sender was never offered.
 */
export const applyTemplateAttachments = (
  selection: AttachmentSelection,
  template?: {
    id?: number | string;
    attachments?: {
      blob_id?: number;
      filename?: string;
      content_type?: string;
      byte_size?: number;
      url?: string;
    }[];
  } | null
): AttachmentSelection => ({
  ...selection,
  appliedTemplateId: template?.id ?? null,
  kept: (template?.attachments ?? [])
    .filter((attachment) => attachment.blob_id !== undefined)
    .map((attachment) => ({
      blobId: attachment.blob_id as number,
      filename: attachment.filename ?? 'Attachment',
      contentType: attachment.content_type,
      byteSize: attachment.byte_size,
      url: attachment.url,
    })),
});

/** Drops whatever a chip's ✕ refers to, leaving the rest of the selection intact. */
export const removeAttachment = (
  selection: AttachmentSelection,
  key: string
): AttachmentSelection => {
  if (key === RESUME_KEY) return { ...selection, includeResume: false };
  if (key === SCORECARDS_KEY) return { ...selection, includeScorecards: false };

  if (key.startsWith('offer:')) {
    const blobId = Number(key.slice('offer:'.length));
    return {
      ...selection,
      offerDocumentBlobIds: selection.offerDocumentBlobIds.filter(
        (id) => id !== blobId
      ),
    };
  }

  if (key.startsWith('attachment:')) {
    const attachmentId = Number(key.slice('attachment:'.length));
    return {
      ...selection,
      atsAttachmentIds: selection.atsAttachmentIds.filter(
        (id) => id !== attachmentId
      ),
    };
  }

  if (key.startsWith('kept:')) {
    const blobId = Number(key.slice('kept:'.length));
    return {
      ...selection,
      kept: selection.kept.filter((a) => a.blobId !== blobId),
    };
  }

  if (key.startsWith('upload:')) {
    const index = Number(key.slice('upload:'.length));
    return {
      ...selection,
      uploads: selection.uploads.filter((_file, i) => i !== index),
    };
  }

  return selection;
};

/** Maps the selection onto the send payload's attachment fields. */
export const toAttachmentPayload = (selection: AttachmentSelection) => ({
  ats_attachment_ids: selection.atsAttachmentIds,
  offer_document_blob_ids: selection.offerDocumentBlobIds,
  include_resume: selection.includeResume,
  include_scorecards: selection.includeScorecards,
  ...(selection.appliedTemplateId
    ? { ats_comm_template_id: selection.appliedTemplateId }
    : {}),
  attachments: [
    ...selection.kept.map((attachment) => attachment.blobId),
    ...selection.uploads,
  ],
});

/**
 * The selection an email already has, for seeding the drawer in edit mode.
 *
 * A save rebuilds the email from the composition it is handed, so anything not seeded
 * here is dropped: the files on it come back as `kept`, and the candidate documents it
 * picked come back as picker state, matched by the blob ids both sides key on.
 */
export const toEditSelection = (
  email: InternalEmail | undefined,
  documents: InternalEmailDocuments | undefined
): AttachmentSelection => {
  const pickedBlobIds = new Set(email?.document_blob_ids ?? []);

  const offerDocumentBlobIds = (documents?.offer_documents ?? [])
    .map((document) => document.blob_id)
    .filter(
      (blobId): blobId is number =>
        blobId !== undefined && pickedBlobIds.has(blobId)
    );

  const atsAttachmentIds = (documents?.other_files ?? [])
    .filter(
      (document) =>
        document.blob_id !== undefined && pickedBlobIds.has(document.blob_id)
    )
    .map((document) => document.ats_attachment_id)
    .filter((id): id is number => id !== undefined);

  const resumeBlobId = documents?.resume?.blob_id;

  return {
    offerDocumentBlobIds,
    atsAttachmentIds,
    includeResume:
      resumeBlobId !== undefined && pickedBlobIds.has(resumeBlobId),
    includeScorecards: Boolean(email?.include_scorecards),
    uploads: [],
    // The email's own files, not a template's — nothing has been applied yet.
    appliedTemplateId: null,
    kept: (email?.attachments ?? [])
      .filter((attachment) => attachment.blob_id !== undefined)
      .map((attachment) => ({
        blobId: attachment.blob_id as number,
        filename: attachment.filename ?? 'Attachment',
        contentType: attachment.content_type,
        byteSize: attachment.byte_size,
        url: attachment.url,
      })),
  };
};
